import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { AuthClientApi } from "../../../../utils/api";
import Pagination from "react-js-pagination";
import { toast } from "react-toastify";

function formatNumber(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export default function RejectedPlansTab() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [companyTotals, setCompanyTotals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  function isMasterBroker() {
    try {
      const clientData = JSON.parse(localStorage.getItem("client_data"));
      return clientData?.client?.is_master_user === true;
    } catch {
      return false;
    }
  }

  const fetchPlans = async (page = 1) => {
    if (!isMasterBroker()) {
      setLoading(false);
      setError("Only Master Broker can perform this action.");
      toast.error("Only Master Broker can perform this action.");
      return;
    }

    try {
      setLoading(true);
      setError(null); // Clear previous errors
      const response = await AuthClientApi.get(
        `/client/blockshares/fetch-rejected-plans?page=${page}&limit=${perPage}`
      );

      const apiData = response.data;

      if (apiData.status && apiData.data && apiData.data.plans) {
        const companiesFromAPI = apiData.data.company_totals.map(
          (ct) => ct.short_name
        );
        setCompanies(companiesFromAPI);
        setCompanyTotals(apiData.data.company_totals);

        // Set pagination metadata from API response
        setTotalRecords(apiData.data.pagination.total || 0);
        setPerPage(apiData.data.pagination.limit || 10);
        setCurrentPage(apiData.data.pagination.page || page);

        const transformedPlans = apiData.data.plans.map((plan) => {
          const amounts = {};
          companiesFromAPI.forEach((company) => {
            amounts[company] = { amount: 0 };
          });

          plan.plan_companies.forEach((pc) => {
            const companyName = pc.Company.short_name;
            amounts[companyName] = { amount: parseFloat(pc.allocated_amount) };
          });

          return {
            id: plan.id,
            tradingNumber: plan.customer?.trading_number || "N/A",
            date: new Date(plan.submitted_at).toLocaleDateString("en-GB"),
            amounts: amounts,
            status: plan.status,
          };
        });

        setPlans(transformedPlans);
      } else {
        console.error("Invalid response structure:", apiData);
        throw new Error("Invalid API response structure");
      }
    } catch (err) {
      console.error("Error fetching rejected plans:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load rejected plans"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans(currentPage);
  }, []);

  const getClickedPageNo = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchPlans(pageNumber);
  };

  const getTotalForCompany = (company) => {
    const companyTotal = companyTotals.find((ct) => ct.short_name === company);
    if (companyTotal) {
      return companyTotal.total_allocated_amount;
    }

    return plans.reduce(
      (sum, plan) => sum + (plan.amounts[company]?.amount || 0),
      0
    );
  };

  const getGrandTotal = () => {
    return companyTotals.reduce(
      (grandTotal, ct) => grandTotal + ct.total_allocated_amount,
      0
    );
  };

  const handleDeleteCompany = (companyToDelete) => {
    setCompanies((prevCompanies) =>
      prevCompanies.filter((company) => company !== companyToDelete)
    );
  };

  return (
    <>
      <Container fluid className="plans-container fade-in">
        {loading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "200px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            Error loading plans: {error}
            <button
              className="btn btn-outline-danger btn-sm ms-2"
              onClick={fetchPlans}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && companies.length === 0 && (
          <div className="alert alert-info" role="alert">
            No rejected plans data available.
          </div>
        )}

        {!loading && !error && companies.length > 0 && (
          <>
            <div className="plans-summary-row">
              <div className="plans-summary-text">
                You have <strong>{plans.length}</strong> rejected plans.
              </div>
            </div>

            <div className="table-responsive custom-scroll">
              <table className="table table-bordered plans-table">
                <thead>
                  <tr className="plans-header-row">
                    <th className="sticky-col">Acc #</th>
                    <th className="sticky-col-2">Date</th>
                    {companies.map((company) => (
                      <th key={company}>
                        <div className="company-col">
                          {company}
                          <button
                            className="btn-transfer-bal"
                            onClick={() => handleDeleteCompany(company)}
                          >
                            Delete
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan, index) => (
                    <tr key={index}>
                      <td className="sticky-col">{plan.tradingNumber}</td>
                      <td className="sticky-col-2">{plan.date}</td>
                      {companies.map((company) => {
                        const amountInfo = plan.amounts[company];
                        const amount = amountInfo ? amountInfo.amount : 0;
                        return (
                          <td key={company}>
                            {amount > 0 ? formatNumber(amount) : 0}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="sticky-col">{plans.length}</td>
                    <td className="sticky-col-2">Total</td>
                    {companies.map((company) => {
                      const total = getTotalForCompany(company);
                      return (
                        <td key={company}>
                          {total > 0 ? formatNumber(total) : 0}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </Container>

      {/* Pagination */}
      {!loading && !error && plans.length > 0 && (
        <div className="pagination-wrapper-right">
          <Pagination
            activePage={currentPage}
            prevPageText="prev"
            nextPageText="next"
            itemClass="page-item"
            linkClass="page-link"
            pageRangeDisplayed={5}
            itemsCountPerPage={perPage}
            totalItemsCount={totalRecords}
            onChange={getClickedPageNo}
          />
        </div>
      )}
    </>
  );
}
